import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import type { Lead } from '@shared/types';
import { formatArrival, formatSince } from '@shared/dates';
import { stageStyle } from '@shared/stageColors';
import { displayPhone, telLink, waLink } from '@shared/phone';
import { useStages } from '../store';
import { sortByDateDesc, type GroupedLeads } from '../filter';
import { Empty, Icons, useDragScroll } from './ui';
import { StagePill } from './StagePill';
import { useMinuteClock } from '../clock';
import { TasksButton } from './Tasks';

/** Anchor just above the tab bar: the KPI tiles and tab switches scroll here. */
export const GROUP_TABS_ANCHOR = 'group-tabs';

/** Scroll so the tab bar sits at the top of the screen, if the page is scrolled past it (or `always`). */
export function scrollToGroupTabs(always = false) {
  const el = document.getElementById(GROUP_TABS_ANCHOR);
  if (!el) return;
  // the bar sticks below whatever header is pinned above it (none on mobile, the app header on desktop)
  const bar = el.nextElementSibling;
  const offset = bar ? parseFloat(getComputedStyle(bar).top) || 0 : 0;
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  if (always || window.scrollY > top) window.scrollTo({ top, behavior: 'auto' });
}

/**
 * Mobile: the groups as tabs — every group and its count stays visible in one sticky, scrollable bar — and the open
 * group's cards below, virtualized in the page scroll.
 */
export function MobileCardList({ groups, onOpen, active, onSelect, filterKey }: {
  groups: GroupedLeads[]; onOpen: (id: string) => void; active: string | null; onSelect: (groupId: string) => void; filterKey: string;
}) {
  const current = groups.find((g) => g.group.id === active) ?? groups[0];
  // A new search or filter that leaves the open tab empty jumps to the first tab with results.
  useEffect(() => {
    if (current && current.leads.length === 0) { const hit = groups.find((g) => g.leads.length > 0); if (hit) onSelect(hit.group.id); }
  }, [filterKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!current) return <p className="p-8 text-center text-ink-3">אין לידים להצגה</p>;
  return (
    <div className="px-3">
      <GroupTabs groups={groups} active={current.group.id} onSelect={(id) => { onSelect(id); scrollToGroupTabs(); }} />
      {current.leads.length === 0
        ? <p className="p-8 text-center text-ink-3">אין לידים בקבוצה הזו</p>
        : <GroupList key={current.group.id} data={current} onOpen={onOpen} />}
    </div>
  );
}

/** The groups as a sticky, scrollable tab bar with counts; `top` is where it sticks (below the desktop header). */
export function GroupTabs({ groups, active, onSelect, top = 0 }: { groups: GroupedLeads[]; active: string; onSelect: (id: string) => void; top?: number }) {
  const barRef = useDragScroll<HTMLDivElement>();
  // keep the open tab in view when it changes (e.g. from a KPI tile)
  useEffect(() => { barRef.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }, [active]);
  return (
    <>
      <div id={GROUP_TABS_ANCHOR} aria-hidden="true" />
      <div ref={barRef} role="tablist" aria-label="קבוצות"
        className="sticky z-20 -mx-3 mb-2 flex gap-1 overflow-x-auto border-b border-line bg-page px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ top, paddingTop: top ? undefined : 'env(safe-area-inset-top)' }}>
        {groups.map((g) => {
          const on = g.group.id === active;
          return (
            <button key={g.group.id} type="button" role="tab" aria-selected={on} onClick={() => onSelect(g.group.id)}
              className={`-mb-px flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 text-sm ${on ? 'border-accent font-semibold text-accent' : 'border-transparent text-ink-2'} ${g.leads.length === 0 && !on ? 'opacity-50' : ''}`}>
              {g.group.name}
              <span className={`rounded-full px-1.5 text-[11px] font-semibold tabular-nums ${on ? 'bg-accent-soft text-accent' : 'bg-panel text-ink-3'}`}>{g.leads.length.toLocaleString('he-IL')}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

/** Card (104px, fixed height) + an 8px gap. */
const CARD_H = 112;

function GroupList({ data, onOpen }: { data: GroupedLeads; onOpen: (id: string) => void }) {
  const rows = sortByDateDesc(data.leads);
  const listRef = useRef<HTMLDivElement>(null);
  const [margin, setMargin] = useState(0);
  // Virtualized against the page scroll: no nested scroll areas on touch.
  useLayoutEffect(() => { setMargin((listRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY); }, [data.leads.length]);
  const v = useWindowVirtualizer({ count: rows.length, estimateSize: () => CARD_H, overscan: 6, scrollMargin: margin });
  return (
    <div ref={listRef} role="tabpanel" aria-label={data.group.name} style={{ height: v.getTotalSize(), position: 'relative' }}>
      {v.getVirtualItems().map((it) => (
        <div key={rows[it.index].id} style={{ position: 'absolute', top: 0, insetInlineStart: 0, width: '100%', transform: `translateY(${it.start - margin}px)`, height: CARD_H, paddingBottom: 8 }}>
          <LeadCard lead={rows[it.index]} onOpen={() => onOpen(rows[it.index].id)} />
        </div>
      ))}
    </div>
  );
}

/** Latin text and digits in Poppins, Hebrew falls through to Heebo. */
const CARD_FONT: React.CSSProperties = { fontFamily: '"Poppins", "Heebo", system-ui, sans-serif' };
const stop = (e: React.MouseEvent) => e.stopPropagation();
const Dot = () => <span className="shrink-0 text-[#6e7d91]/60" aria-hidden="true">·</span>;
/**
 * White, rounded, bordered — all three action buttons share it. 48px from 420px up; 40px on narrow phones, where the
 * ::before still makes it a 44px tap target.
 */
const ACTION = "relative inline-flex h-10 w-10 min-[420px]:h-12 min-[420px]:w-12 shrink-0 items-center justify-center rounded-xl border border-[#e1e4ea] bg-white transition-transform active:scale-95 before:absolute before:-inset-0.5 before:content-['']";
const ICON = 'h-[22px] w-[22px] min-[420px]:h-6 min-[420px]:w-6';

/**
 * The mobile lead card.
 * Line 1 spans the whole card: name · number at the start (right), the status pill in the end (left) corner — so the name
 * runs above the buttons and practically never truncates. Lines 2–3 ("נכנס לפני …" · "ב- DD.MM בשעה HH:MM", then the
 * source) sit beside the three action buttons, which span both lines: call, WhatsApp, and open-lead with the notes count
 * inside the bubble. Line 1 is the largest; 2 and 3 share one smaller size. The whole card opens the lead too.
 */
export function LeadCard({ lead, onOpen }: { lead: Lead; onOpen: () => void }) {
  const { stageName, meta } = useStages();
  const now = useMinuteClock();
  const dateOnly = !!meta?.tenant?.settings?.forecastDatePipelineIds?.includes(lead.pipelineId);
  const st = stageStyle(stageName(lead.stageId));
  const phone = displayPhone(lead.phone);
  const tel = telLink(lead.phone);
  const wa = waLink(lead.phone, meta?.tenant.settings.whatsappMessage);
  const since = formatSince(lead.leadDate, now);
  const arrival = formatArrival(lead.leadDate, { dateOnly });
  // Below 480px the full second line doesn't fit beside three buttons: same facts, fewer words.
  const sinceShort = formatSince(lead.leadDate, now, { compact: true });
  const arrivalShort = formatArrival(lead.leadDate, { compact: true, dateOnly });
  return (
    <div className="relative h-full overflow-hidden rounded-xl" style={CARD_FONT}>
      <article
        className={`relative flex h-full cursor-pointer flex-col gap-1.5 overflow-hidden rounded-xl border border-[#e1e4ea]/60 bg-white py-3 pe-3 ps-4 ${st.dimmed ? 'opacity-70' : ''}`}
        onClick={onOpen}
      >
        <div className="absolute inset-y-0 start-0 w-[3px]" style={{ background: st.stripe }} aria-hidden="true" />

        {/* Line 1: the name block keeps its natural width; the status shrinks first (factor 100 vs 1), so the name only
            truncates when it alone is wider than the card. */}
        <div className="flex items-center gap-2">
          <div className="flex min-w-0 items-center gap-1.5 text-[15px] leading-6 [flex:0_1_auto]">
            <h3 dir="auto" className="min-w-0 truncate font-semibold text-[#0f1729]">{lead.fullName || <Empty />}</h3>
            {phone && <><Dot /><span className="ltr shrink-0 font-medium tabular-nums text-[#0f1729]/80">{phone}</span></>}
          </div>
          <div className="ms-auto flex min-w-11 justify-end [flex:0_100_auto]"><StagePill lead={lead} variant="card" onChanged={onOpen} /></div>
        </div>

        <div className="flex min-h-0 flex-1 items-center gap-3">
          <div className="grid min-w-0 flex-1 gap-1 text-[12px] leading-5 text-[#6e7d91]">
            <p className="truncate text-start">
              <span className="max-[479px]:hidden">{since || '—'}{arrival && <> <Dot /> {arrival}</>}</span>
              <span className="min-[480px]:hidden">{sinceShort || '—'}{arrivalShort && <> <Dot /> <span className="ltr">{arrivalShort}</span></>}</span>
            </p>
            {/* right-aligned like the rest, but the truncating span has its own direction, so a Latin source is cut at its end */}
            <p className="flex min-w-0"><span dir="auto" className="min-w-0 truncate">{lead.source || '\u00a0'}</span></p>
          </div>

          <div className="flex shrink-0 items-center gap-1.5 min-[420px]:gap-2">
            {tel && (
              <a href={tel} onClick={stop} className={`${ACTION} text-[#18a5dc]`} aria-label={`חיוג ל-${phone}`} title="חיוג">
                <Icons.PhoneLine className={ICON} />
              </a>
            )}
            {wa && (
              <a href={wa} target="_blank" rel="noopener noreferrer" onClick={stop} className={`${ACTION} text-[#22c35d]`} aria-label={`WhatsApp ל-${phone}`} title="WhatsApp">
                <Icons.WhatsAppGlyph className={ICON} />
              </a>
            )}
            <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(); }} className={`${ACTION} text-[#18a5dc]`}
              aria-label={lead.noteCount ? `פתח ליד (${lead.noteCount} הערות)` : 'פתח ליד'} title="פתח ליד">
              <Icons.ChatCount count={lead.noteCount} className="h-[22px] w-6 min-[420px]:h-6 min-[420px]:w-[26px]" />
            </button>
            {/* left of the comments button: open-task count; opens the add-task form */}
            <TasksButton lead={lead} card={{ className: `${ACTION} text-[#18a5dc]`, iconClassName: ICON }} />
          </div>
        </div>
      </article>
    </div>
  );
}
